import { useEffect, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Segmented from "@/components/Segmented";
import { toast } from "sonner";
import { opsApi, money } from "../../lib/api";

const TABS = [
  { key: "estimate", label: "Estimate" },
  { key: "frs", label: "Current FRS" },
  { key: "diff", label: "FRS change" },
  { key: "manual", label: "Type a line" },
];
const TH = "px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground";
const QTY_INPUT = "mx-auto h-7 w-20 text-center text-xs tabular-nums";

/** Lines never get typed twice: they come off the working estimate (full cost library), off the
 *  Current FRS, or out of the delta between two FRS versions. The PM enters the quantity being
 *  priced — positive adds, negative deducts. */
export default function AddLinesDialog({ projectId, co, onClose, onDone }) {
  const [tab, setTab] = useState("estimate");
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [estimate, setEstimate] = useState(null);
  const [frs, setFrs] = useState(null);
  const [picked, setPicked] = useState({});   // id -> { on, qty }
  const [manual, setManual] = useState({ description: "", manufacturer: "", item_number: "",
                                          qty: 1, unit_cost: 0, uom: "ea", line_kind: "material" });

  useEffect(() => {
    if (tab === "estimate" && !estimate) {
      opsApi.estimate(projectId, "working").then(setEstimate)
        .catch(() => toast.error("Could not load the working estimate"));
    }
    if (tab === "frs" && !frs) {
      opsApi.frsItems(projectId, { limit: 500 }).then(setFrs)
        .catch(() => toast.error("Could not load the FRS"));
    }
  }, [tab, projectId, estimate, frs]);

  const q = search.trim().toLowerCase();
  const estLines = (estimate?.lines || []).filter(
    (l) => !q || `${l.description} ${l.manufacturer} ${l.item_number}`.toLowerCase().includes(q));
  const frsRows = (frs?.items || []).filter(
    (i) => !q || `${i.product_description} ${i.product_code} ${i.room_number} ${i.manufacturer}`
      .toLowerCase().includes(q));

  // Ticked rows with a non-zero CO qty; the sign of the qty decides add vs deduct.
  const chosen = (idKey) => Object.entries(picked)
    .filter(([, v]) => v.on && Number(v.qty))
    .map(([id, v]) => ({ [idKey]: id, qty: Math.abs(Number(v.qty)), sign: Number(v.qty) < 0 ? "deduct" : "add" }));

  const submit = async () => {
    setBusy(true);
    try {
      let res;
      if (tab === "estimate") {
        const lines = chosen("estimate_line_id");
        if (!lines.length) { toast.error("Tick at least one line and give it a CO quantity"); setBusy(false); return; }
        res = await opsApi.coAddFromEstimate(projectId, co.id, lines);
      } else if (tab === "frs") {
        const items = chosen("frs_item_id");
        if (!items.length) { toast.error("Tick at least one FRS row and give it a CO quantity"); setBusy(false); return; }
        res = await opsApi.coAddFromFrs(projectId, co.id, items);
      } else if (tab === "diff") {
        res = await opsApi.coAddFromFrsDiff(projectId, co.id, { a: "original", b: "current" });
        toast.info(`FRS delta: ${res.diff_counts.added} added, ${res.diff_counts.removed} removed, `
          + `${res.diff_counts.changed} changed`);
      } else {
        if (!manual.description.trim()) { toast.error("Give the line a description"); setBusy(false); return; }
        if (!Number(manual.qty)) { toast.error("Enter a quantity — negative to deduct"); setBusy(false); return; }
        res = await opsApi.coAddManualLine(projectId, co.id, {
          ...manual, qty: Number(manual.qty), unit_cost: Number(manual.unit_cost) });
      }
      toast.success("Lines added");
      onDone(res.change_order || res);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Those lines could not be added");
    } finally { setBusy(false); }
  };

  // Ticking never guesses a quantity — the PM types the one being priced; typing a qty ticks the row.
  const toggle = (id) => setPicked((p) => ({
    ...p, [id]: { on: !p[id]?.on, qty: p[id]?.qty ?? "" } }));
  const setQty = (id, qty) => setPicked((p) => ({ ...p, [id]: { on: qty !== "" && qty !== "-", qty } }));
  const pickedCount = Object.values(picked).filter((v) => v.on && Number(v.qty)).length;

  const qtyCell = (id, testId) => (
    <Input type="number" step="any" value={picked[id]?.qty ?? ""} placeholder="0"
           onChange={(e) => setQty(id, e.target.value)} data-testid={testId}
           className={`${QTY_INPUT} ${Number(picked[id]?.qty) < 0 ? "text-rose-600 dark:text-rose-400" : ""}`} />
  );

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col gap-0 p-5" data-testid="ops-co-add-lines-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="text-base">Add to {co.number}</DialogTitle>
          <DialogDescription className="text-xs">
            Everything but the quantity travels with the line — manufacturer, product, cost, cost code, unit
            and the markup rules. Enter the quantity being priced: positive adds, negative deducts.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {(tab === "estimate" || tab === "frs") && (
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…"
                     className="h-8 w-56 pl-8 text-xs" data-testid="ops-co-add-search" />
            </div>
          )}
          <Segmented value={tab} onChange={(k) => { setTab(k); setPicked({}); }}
                     options={TABS.map((t) => ({ ...t, testId: `ops-co-add-tab-${t.key}` }))} />
        </div>

        <div className="mt-3 min-h-0 flex-1 overflow-y-auto rounded-lg border border-border">
          {tab === "estimate" && (
            !estimate ? <div className="p-6 text-center text-xs text-muted-foreground">Loading…</div> : (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted/60 backdrop-blur">
                  <tr><th className="w-8 px-2 py-1.5" /><th className={TH}>Description</th>
                    <th className={TH}>Manufacturer</th>
                    <th className={`${TH} text-right`}>Unit cost</th>
                    <th className={`${TH} text-right`}>Est. qty</th>
                    <th className={`${TH} text-center`}>CO qty</th></tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {estLines.map((l) => (
                    <tr key={l.id} data-testid={`ops-co-add-est-${l.id}`} className={picked[l.id]?.on ? "bg-[#007BFF]/5" : ""}>
                      <td className="px-2 py-1.5"><input type="checkbox" checked={!!picked[l.id]?.on}
                        onChange={() => toggle(l.id)} data-testid={`ops-co-add-est-check-${l.id}`} /></td>
                      <td className="px-3 py-1.5">{l.description}</td>
                      <td className="px-3 py-1.5 text-muted-foreground">{l.manufacturer}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{money(l.effective_unit_cost ?? l.unit_cost)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground" data-testid={`ops-co-add-est-orig-${l.id}`}>{l.qty}</td>
                      <td className="px-3 py-1.5 text-center">{qtyCell(l.id, `ops-co-add-est-qty-${l.id}`)}</td>
                    </tr>
                  ))}
                  {estLines.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No estimate lines match.</td></tr>}
                </tbody>
              </table>
            ))}

          {tab === "frs" && (
            !frs ? <div className="p-6 text-center text-xs text-muted-foreground">Loading…</div> : (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted/60 backdrop-blur">
                  <tr><th className="w-8 px-2 py-1.5" /><th className={TH}>Room</th>
                    <th className={TH}>Product</th>
                    <th className={TH}>Manufacturer</th>
                    <th className={`${TH} text-right`}>FRS qty</th>
                    <th className={`${TH} text-center`}>CO qty</th></tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {frsRows.map((i) => (
                    <tr key={i.id} data-testid={`ops-co-add-frs-${i.id}`} className={picked[i.id]?.on ? "bg-[#007BFF]/5" : ""}>
                      <td className="px-2 py-1.5"><input type="checkbox" checked={!!picked[i.id]?.on}
                        onChange={() => toggle(i.id)} data-testid={`ops-co-add-frs-check-${i.id}`} /></td>
                      <td className="px-3 py-1.5">{[i.building, i.floor, i.room_number].filter(Boolean).join(" · ")}</td>
                      <td className="px-3 py-1.5">{i.product_description || i.product_code}</td>
                      <td className="px-3 py-1.5 text-muted-foreground">{i.manufacturer}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground" data-testid={`ops-co-add-frs-orig-${i.id}`}>{i.quantity}</td>
                      <td className="px-3 py-1.5 text-center">{qtyCell(i.id, `ops-co-add-frs-qty-${i.id}`)}</td>
                    </tr>
                  ))}
                  {frsRows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No FRS rows match.</td></tr>}
                </tbody>
              </table>
            ))}

          {tab === "diff" && (
            <div className="p-6 text-xs text-muted-foreground" data-testid="ops-co-add-diff">
              Pulls the delta between the <b className="text-foreground">Original</b> and the <b className="text-foreground">Current</b> FRS
              and turns it into change order lines — added rooms become adds, removed rooms become deducts and
              quantity moves come across as the difference. Each line keeps its room, so the FRS sync afterwards
              knows exactly what changed.
            </div>
          )}

          {tab === "manual" && (
            <div className="grid gap-2 p-4 sm:grid-cols-2" data-testid="ops-co-add-manual">
              {[["description", "Description"], ["manufacturer", "Manufacturer"],
                ["item_number", "Item #"], ["uom", "Unit"]].map(([k, label]) => (
                <div key={k}>
                  <label className="text-xs text-muted-foreground">{label}</label>
                  <Input value={manual[k]} onChange={(e) => setManual({ ...manual, [k]: e.target.value })}
                         className="mt-1 h-9 text-sm" data-testid={`ops-co-manual-${k}`} />
                </div>
              ))}
              <div>
                <label className="text-xs text-muted-foreground">Qty (negative to deduct)</label>
                <Input type="number" step="any" value={manual.qty}
                       onChange={(e) => setManual({ ...manual, qty: e.target.value })}
                       className="mt-1 h-9 text-sm tabular-nums" data-testid="ops-co-manual-qty" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Unit cost (vendor cost, never sell)</label>
                <Input type="number" step="0.01" value={manual.unit_cost}
                       onChange={(e) => setManual({ ...manual, unit_cost: e.target.value })}
                       className="mt-1 h-9 text-sm tabular-nums" data-testid="ops-co-manual-cost" />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="mt-4 items-center sm:justify-between">
          <span className="text-xs text-muted-foreground" data-testid="ops-co-add-count">
            {(tab === "estimate" || tab === "frs") && pickedCount > 0 ? `${pickedCount} line${pickedCount === 1 ? "" : "s"} ready` : ""}
          </span>
          <div className="flex gap-2">
            <Button variant="nav" size="sm" className="text-xs" onClick={onClose}>Cancel</Button>
            <Button size="sm" className="text-xs" disabled={busy} onClick={submit} data-testid="ops-co-add-submit">
              {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />} Add to CO
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
